import React, { FC, useCallback } from 'react';
import { useTranslations } from '@lang';
import { LeftSideBarWrapper } from '@common/components/BookingForms/Modal';
import { Availability, GuestDetails, Customise } from '@common/features';
import { useRouter } from 'shared-routing';
import { SpaEntityFormStepsProps, SpaEntityFormStepsReduxProps } from './SpaEntityFormSteps.types';
import { Moment } from 'moment';

export const SpaEntityFormSteps: FC<SpaEntityFormStepsProps & SpaEntityFormStepsReduxProps> = ({
  spaEntity,
  guest,
  journeyParams,
  existingGuests,
  maxDaysAdvance,
  property,
  bookingStep = 1,
  isSignInModalOpen = false,
  isExtrasAvailable = false,
  goToNextStep,
  finishWizardAction,
  handleSignInModalOpen,
  fetchingDates,
  showGenderNeutral,
  isSpaEntityFetching,
  SpaEntityFormStepEnum,
  entityBookingRoute,
  entityType,
  entityLabel,
  availableTimeslots,
  fetchSpaEntity,
  language,
  setStartDate,
}) => {
  const router = useRouter();
  const { t } = useTranslations();
  const STICKY_BAR_ID = 'spa-entity-sticky-bar';
  const { id, genderSpecific, minimumGuests, extras, availability, telephoneOnly } =
    spaEntity || {};
  const handleInvalidConfig = useCallback(() => {
    router.push({
      pathname: entityBookingRoute,
      query: {
        id: id,
        bookingStep: SpaEntityFormStepEnum.AVAILABILITY,
      },
    });
  }, [router, id]);

  const timeslots = availableTimeslots || availability;
  const isModalOpen = !!bookingStep;

  const fetchSpaEntityAvailability = (arrive: Moment) => {
    if (fetchSpaEntity) {
      fetchSpaEntity(property.id, id, language, arrive);
    }
  };

  const Gender = {
    M: 'male',
    F: 'female',
  };

  const guests = new Array(minimumGuests).fill({
    firstName: '',
    lastName: '',
    gender: genderSpecific ? Gender[genderSpecific] || '' : '',
  });

  const initialValues = {
    startDate: journeyParams.spaDate || journeyParams.fromDate,
    guests,
  };

  const isPaidExtras = (extras) => extras?.price?.amount > 0;

  const paidExtras = extras
    ?.filter((extras) => isPaidExtras(extras))
    .sort((a, b) => b.featured - a.featured);
  const freeExtras = extras
    ?.filter((extras) => !isPaidExtras(extras))
    .sort((a, b) => b.featured - a.featured);

  const typeName = t(entityLabel);
  const formSteps = {
    [SpaEntityFormStepEnum.AVAILABILITY]: (
      <LeftSideBarWrapper key={SpaEntityFormStepEnum.AVAILABILITY}>
        <Availability
          stickyBarId={STICKY_BAR_ID}
          isModalOpen={isModalOpen}
          onSubmit={goToNextStep}
          spaEntity={spaEntity}
          allTimeslots={timeslots}
          initialValues={initialValues}
          maxDaysAdvance={maxDaysAdvance}
          property={property}
          entityType={entityType}
          fetchingDates={fetchingDates}
          fetchAvailability={fetchSpaEntityAvailability}
          setStartDate={setStartDate}
        />
        {!telephoneOnly && (
          <GuestDetails
            guest={guest}
            spaEntity={spaEntity}
            typeName={typeName}
            stickyBarId={STICKY_BAR_ID}
            onSubmit={isExtrasAvailable ? goToNextStep : finishWizardAction}
            existingGuests={existingGuests}
            isSignInModalOpen={isSignInModalOpen}
            handleSignInModalOpen={handleSignInModalOpen}
            handleInvalidConfig={handleInvalidConfig}
            showGenderNeutral={showGenderNeutral}
            isItemFetching={isSpaEntityFetching}
          />
        )}
      </LeftSideBarWrapper>
    ),
    [SpaEntityFormStepEnum.CUSTOMISE]: isExtrasAvailable ? (
      <LeftSideBarWrapper key={SpaEntityFormStepEnum.CUSTOMISE}>
        <Customise
          spaEntity={spaEntity}
          stickyBarId={STICKY_BAR_ID}
          onSubmit={finishWizardAction}
          freeExtras={freeExtras}
          paidExtras={paidExtras}
          handleInvalidConfig={handleInvalidConfig}
        />
      </LeftSideBarWrapper>
    ) : null,
  };

  return formSteps[bookingStep] || <></>;
};
